import * as React from 'react';
import { cn } from '../utils/cn';
import { Star } from 'lucide-react';

export interface RateProps {
  /** Current value (1-5) */
  value?: number;
  /** Default value */
  defaultValue?: number;
  /** Number of stars */
  count?: number;
  /** Change handler */
  onChange?: (value: number) => void;
  /** Disable interaction */
  disabled?: boolean;
  /** Allow half stars */
  allowHalf?: boolean;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
}

export function Rate({
  value: controlledValue,
  defaultValue = 0,
  count = 5,
  onChange,
  disabled = false,
  className,
  style,
}: RateProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const [hoverValue, setHoverValue] = React.useState(0);

  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const displayValue = hoverValue || value;

  const handleClick = (starValue: number) => {
    if (disabled) return;
    setInternalValue(starValue);
    onChange?.(starValue);
  };

  return (
    <div
      className={cn('inline-flex gap-1', disabled && 'opacity-60', className)}
      style={style}
      role="radiogroup"
      aria-label="Betyg"
    >
      {Array.from({ length: count }, (_, i) => {
        const starValue = i + 1;
        const isFilled = starValue <= displayValue;

        return (
          <button
            key={i}
            type="button"
            disabled={disabled}
            onClick={() => handleClick(starValue)}
            onMouseEnter={() => !disabled && setHoverValue(starValue)}
            onMouseLeave={() => setHoverValue(0)}
            className={cn(
              '!p-0 !h-auto !border-0 !bg-transparent !tracking-normal !normal-case',
              'transition-colors duration-fast',
              !disabled && 'cursor-pointer hover:scale-110',
              isFilled ? 'text-yellow-400' : 'text-gray-300'
            )}
            aria-label={`${starValue} stjärnor`}
          >
            <Star
              size={24}
              fill={isFilled ? 'currentColor' : 'none'}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
}
